<template>
  <div
    ref="container"
    class="col-sm-12 nopads"
  >
    <div class="col-sm-12 nopads pt-2 px-2">
        <!-- Contract selector -->
        <div class="row nopads">
            <div class="col-xl-5 pl-1 pr-1">
                <open-contract-filter
                    :to="toDate?.toISOString()"
                    :from="fromDate?.toISOString()"
                    :show-company="false"
                    :show-only-my-contracts-option="true"
                    :contract="preSelectedContract"
                    @loading="loading=true"
                    @loading-completed="loading=false"
                    @contract-selected="setContract"
                />
            </div>
        </div>

        <!-- Place holder for no contracts -->
        <div v-if="!contract" class="bg-white rounded-lg p-12 my-2">
            <div class="flex flex-col items-center justify-center text-center text-gray-500 max-w-md mx-auto">
                <font-awesome-icon icon="calendar" style="font-size: 5em; color: #d9d9d9; margin-top: 2em; margin-bottom: .2em"/>
                <h2 class="text-xl font-medium mb-2" style="color: #bcbcbc">{{ $t('contract_diary.select_contract')}}</h2>
                <span class="text-sm" style="color: #bcbcbc">{{ $t('contract_diary.select_contract_hint' )}}</span>
            </div>
        </div>

        <!-- Calendar and diary form -->
        <div v-else class="row my-4 px-1">
            <div class="col-xl-5 px-1 mt-2">
                <div class="p-1" style="border: 1px solid #BCBCBC; border-radius: .5em; height: 100%">
                    <contract-diary-calendar
                        ref="calendar"
                        :diaries="results"
                        @dateSelect="dateSelected"
                        @monthChanged="initDates"
                    />
                </div>
            </div>
            <div class="col-xl-7 mt-2 px-1">
                <Transition name="fade" mode="out-in">
                    <!-- New contract diary -->
                    <div v-if="contractDiary && (editMode || contractDiary.id < 0)" class="p-1"  style="border: 1px solid #BCBCBC; border-radius: .5em; height: 100%">
                        <contract-diary-editor
                            v-model="contractDiary"
                            ref="editor"
                            :report-list-view="false"
                            :show-ely-fields="contract.ely_contract"
                            :pre-selected-contract="preSelectedContract"
                            :show-contract-selection="false"
                            @close="restoreDate"
                            @close-and-update="closeAndUpdate"
                        />
                    </div>
                    <!-- Existing diary details - either city or ely -->
                    <div v-else-if="contractDiary">
                        <div v-if="contract.ely_contract">
                            <contract-work-diary
                                :contract="contract"
                                :date="selectedDay"
                                :show-edit="hasObserver(user.roles)"
                                @edit="editMode = true"
                                @update="closeAndUpdate"
                            />
                        </div>
                        <div v-else>
                            <city-contract-diary
                                :contract="contract"
                                :date="selectedDay"
                                :user="user"
                                :show-edit="hasObserver(user.roles)"
                                @edit="editMode = true"
                                @update="closeAndUpdate"
                            />
                        </div>
                    </div>

                    <div v-else class="text-center" style="margin-top: 8em;" >
                        <h3 class="text-xl font-medium mb-2" style="color: #bcbcbc">{{ $t('contract_diary.select_day') }}</h3>
                        <span class="text-sm" style="color: #bcbcbc">
                           {{ $t('contract_diary.select_day_hint') }}
                        </span>
                    </div>
                </Transition>
            </div>
        </div>

      <div
        v-if="loading"
        id="loader"
        class="spinner"
      />
    </div>
  </div>
</template>

<script>
import {timeUtils} from "@/components/mixins/TimeUtils";
import {restApi} from "@/components/mixins/RestApiMixin";
import {workManagementHelper} from '@/components/mixins/WorkManagementMixin'
import ContractDiaryList from "@/components/contractreport/ContractDiaryList.vue";
import ContractDiaryEditor from "@/components/contractreport/ContractDiaryEditor.vue";
import ContractDiarySearch from "@/components/contractreport/ContractDiarySearch";
import BaseModal from "@/components/BaseModal";
import {inject} from "vue";
import {userHelper} from "../mixins/UserMixin.js";
import OpenContractFilter from "../contract/OpenContractFilter.vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import ContractDiaryCalendar from "./ContractDiaryCalendar.vue";
import CityContractDiary from "./CityContractDiary.vue";
import ContractWorkDiary from "../contract/external/ContractWorkDiary.vue";

export default {
    name: "ContractDiariesView",
    components: {
        ContractWorkDiary,
        CityContractDiary,
        ContractDiaryCalendar,
        FontAwesomeIcon,
        OpenContractFilter, ContractDiaryList, ContractDiarySearch, ContractDiaryEditor, BaseModal},
    mixins: [timeUtils, restApi, workManagementHelper, userHelper],
    props: {
        preSelectedContract: {
            type: Object,
            default: null
        },
        user: {
            type: Object,
            required: true
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    computed: {
        selectedDay() {
            return this.$refs.calendar?.getSelectedDay()
        }
    },
    data: function () {
        return {
            fromDate: null,
            toDate: null,
            editMode: false,
            contractDiary: null,
            lastSearchedParams: null,
            contract: null,
            results: [],
            isElyContract: false,
            loading: false
        }
    },
    created() {
        this.initDates(new Date())
    },
    methods: {
        initDates(date) {
            // First day of current month at 00:00:00
            const firstDay = date;
            firstDay.setDate(1);
            firstDay.setHours(0, 0, 0, 0);
            this.fromDate = firstDay

            // Last day of current month at 23:59:59
            const lastDay = new Date(firstDay);
            lastDay.setMonth(lastDay.getMonth() + 1);
            lastDay.setDate(0);
            lastDay.setHours(23, 59, 59, 999);
            this.toDate = lastDay

            this.contractDiary = null
        },

        fetchDiaries: function () {
            this.results = []
            this.isElyContract = this.contract.ely_contract
            let params = {
                contract: this.contract.id,
                from: this.fromDate,
                to: this.toDate,
            }
            this.lastSearchedParams = params
            this.loading = true
            this.restFetchParams(this.contractDiaryUrl, params, this.handleResponse)

        },
        handleResponse: function (response) {
            if (response && response.data) {
                this.results = response.data
            }
            this.loading = false
        },
        closeAndUpdate: function () {
            this.contractDiary = null
            this.editMode = null
            this.fetchDiaries()
        },
        restoreDate() {
            this.editMode = false
            this.handleDateChange(this.$refs.calendar.getSelectedDay())
        },
        setContract(contract) {
            this.contract = contract
            this.contractDiary = null
            if (contract) {
                this.fetchDiaries()
            }
        },
        dateSelected(date) {
            // Do not change if there are changes in the editor!!
            if (this.$refs.editor?.isDataChanged()) {
                this.$refs.editor.showConfirmDialog()
            } else {
                this.handleDateChange(date)
            }
        },

        handleDateChange(date) {
            this.editMode = false
            if (!date) {
                this.contractDiary = null
                return
            }
            const diary = this.results.find(diary => {
                const diaryDay = new Date(diary.record_date)
                return diaryDay.getDate() === date.getDate() &&
                    diaryDay.getMonth() === date.getMonth() &&
                    diaryDay.getFullYear() === date.getFullYear()
            })
            if (diary) {
                // Opens the diary details mode
                this.contractDiary = diary
            } else {
                // Initializes editor with new diary for observers
                this.contractDiary = this.hasObserver(this.user?.roles) ? this.newContractDiary(date, this.contract) : null
            }
        },

    }
}
</script>
